import {
  ChangeDetectionStrategy,
  Component,
  QueryList,
  Renderer2,
  ViewChildren,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  CcyPairLookupService,
  CcyPairPromiseLookupService,
  MarketTimePromiseLookupService,
  MarketTimeLookupService,
  ProfileSettingKeys,
  ActiveUser,
  ClientPromiseLookupService,
  AccountPromiseLookupService,
  BranchLookupService,
  ClientAccountVisibilityType,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { BfxUserSettingsComponent } from "../bfx-user-settings/bfx-user-settings.component";
import { ICPrivateOrderDefaultsComponent } from "../bfx-element-library/ic-private-order-defaults.component";
import { BfxAmountSpinnerComponent } from "../bfx-interface-controls/bfx-amount-spinner.component";
import { BfxInputControlStateEnum } from "../bfx-interface-controls/models/bfx-interface-controls.enums";
import { BfxClientIntentToggleComponent } from "../bfx-interface-controls/bfx-toggle-client-intent.component";
import { OEApiService } from "../order-entry/services/oe-api.service";
import { BfxOrderEntrySettingsComponent } from "./bfx-order-entry-settings.component";
import { OEDefaultsModel } from "./model/bfx-order-entry-defaults.model";
import {
  ICAutocompleteGenericModel,
  ICFixedCcyModel,
} from "../order-entry/interface/controls/models/bfx-interface-controls.models";
import { ICAutocompleteDefaultsComponent } from "../bfx-element-library/ic-autocomplete-defaults.component";
import { ICCcyPairDefaultsComponent } from "../bfx-element-library/ic-ccy-pair-defaults.component";
import {
  BfxAutoCompleteComponent,
  BfxAutoCompleteComponentState,
} from "../bfx-element-library/bfx-autocomplete.component";
import {
  IBfxUserSettingsPageSubComponent,
  BfxUserSettingsValidityState,
} from "./bfx-user-settings-page.component";
import { BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import { ICFixedCcyComponent } from "../ic-controls/ic-fixed-ccy.component";
import { OENInputControl } from "../order-entry/data/enums/oe-input-control.enum";
import { ICNInputControlState } from "app/order-entry/interface/controls/models/bfx-interface-controls.enums";
import { NumberSpinnerComponent } from "shared/base-components/number-spinner.component";
import { BfxOrderEntryDefaultsService } from "app/bfx-user-settings/support/bfx-order-entry-defaults.service";

const PROFILE_KEY = ProfileSettingKeys.userOrderEntryGammaSettings;
const TAB_PROFILE_KEY = ProfileSettingKeys.userOrderEntryActiveTabsSettings;
const ALLOWED_TABS = ["id", "name"];

@Component({
  selector: "bfx-order-entry-settings-gamma",
  templateUrl: "./html/bfx-order-entry-settings-gamma.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderEntrySettingsGammaComponent
  implements IBfxUserSettingsPageSubComponent<BfxOrderEntrySettingsComponent>, AfterViewInit
{
  /**********************************************************************/
  /* DATA */

  public dataChanged: boolean = false;
  public parentComponent: BfxUserSettingsComponent = null;
  public activeSubComponent: number = 0;
  public categoryComponent: BfxOrderEntrySettingsComponent = null;
  //
  private entryDataToUpdate: string = "";
  private tabDataToUpdate: string = "";
  //
  private fwdEnabled: boolean = false;
  private defaultTenor: CubiqTS.Tenor = CubiqTS.Tenor.SPOT;
  private defaultValueDate: ICAutocompleteGenericModel = null;

  /*---------- Interface References ---------- */
  @ViewChildren("inputClient")
  icClient: QueryList<ICAutocompleteDefaultsComponent>;
  @ViewChildren("inputAccount")
  icAccount: QueryList<ICAutocompleteDefaultsComponent>;
  @ViewChildren("inputCcyPair")
  icCcyPair: QueryList<ICCcyPairDefaultsComponent>;
  @ViewChildren("inputOrderType")
  icOrderType: QueryList<ICAutocompleteDefaultsComponent>;
  @ViewChildren("toggleClientIntent")
  icClientIntent: QueryList<BfxClientIntentToggleComponent>;
  @ViewChildren("toggleFixedCcy") icFixedCcy: QueryList<ICFixedCcyComponent>;
  @ViewChildren("inputOrderAmount")
  icOrderAmount: QueryList<BfxAmountSpinnerComponent>;
  @ViewChildren("inputValueDate")
  icValueDate: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputGoodFrom")
  icGoodFrom: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputGoodUntil")
  icGoodUntil: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputPrivateOrder")
  icPrivateOrder: QueryList<ICPrivateOrderDefaultsComponent>;

  @ViewChildren("inputDelta")
  deltaControl: QueryList<NumberSpinnerComponent>;

  @ViewChildren("inputRungsAbove")
  rungsAboveControl: QueryList<NumberSpinnerComponent>;

  @ViewChildren("inputRungsBelow")
  rungsBelowControl: QueryList<NumberSpinnerComponent>;

  public data: OEDefaultsModel = new OEDefaultsModel();
  public loadedData: OEDefaultsModel = new OEDefaultsModel();
  public loadedTabData: {
    client: string;
    account: string;
  } = {
    client: null,
    account: null,
  };

  public control: {
    showIntentAndAmount: boolean;
    isLoading: boolean;
    isInitializing: boolean;
    clientAccountConstraintAutoSelect: boolean;
  } = {
    showIntentAndAmount: true,
    isLoading: false,
    isInitializing: true,
    clientAccountConstraintAutoSelect: true,
  };

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    private translate: TranslateService,
    private api: OEApiService,
    private currencyPairService: CcyPairPromiseLookupService,
    private clientLookup: ClientPromiseLookupService,
    private accountLookup: AccountPromiseLookupService,
    private defaultsService: BfxOrderEntryDefaultsService,
    public renderer2: Renderer2,
    protected branchLookupService: BranchLookupService,
    protected cd: ChangeDetectorRef,
  ) {
    this.initializeSettings();
    this.initializeLabelTranslations();
    this.initializeDatasets();
  }

  ngAfterViewInit() {}

  /**********************************************************************/
  /* DATASET INITIALIZATION */

  private initializeSettings(): void {
    // more
    let moreSettings = this.api.getSystemConfigForMorePanel();
    this.data.privateOrder.show = this.data.privateOrder.showConfig = moreSettings.showPrivateOrder;
    this.fwdEnabled = moreSettings.fwdEnabled;
  }

  private initializeLabelTranslations(): void {
    this.data.currencyPair.label = this.translate.instant(this.data.currencyPair.labelKey);
    this.data.clientIntent.label = this.translate.instant(this.data.clientIntent.labelKey);
    this.data.goodFrom.label = this.translate.instant(this.data.goodFrom.labelKey);
    this.data.goodUntil.label = this.translate.instant(this.data.goodUntil.labelKey);
    this.data.goodFrom.tooltip = this.translate.instant(this.data.goodFrom.tooltipKey);
    this.data.goodUntil.tooltip = this.translate.instant(this.data.goodUntil.tooltipKey);
    this.data.privateOrder.label = this.translate.instant(this.data.privateOrder.labelKey);
    this.data.delta.label = this.translate.instant(this.data.delta.labelKey);
    this.data.rungsAbove.label = this.translate.instant(this.data.rungsAbove.labelKey);
    this.data.rungsBelow.label = this.translate.instant(this.data.rungsBelow.labelKey);
  }

  public initializeDatasets(): void {
    this.initializeOrderTypeDataset();
    this.initializeCurrencyPairs();
    this.initializeMarketTimes();
  }

  private initializeCurrencyPairs(): void {
    let dataset = this.api.getGammaCcyPairsDataset();
    if (dataset != null && dataset instanceof Array && dataset.length > 0) {
      this.data.currencyPair.dataset = Object.assign([], dataset);
    } else {
      logger.out.error(
        "ERROR: spotDefaults.initializeCurrencyPairs(): CcyPairs dataset load failed!",
      );
      this.data.currencyPair.dataset = [];
    }
  }

  private initializeOrderTypeDataset() {
    if (
      this.data.orderType.fullDataset == null ||
      (Array.isArray(this.data.orderType.fullDataset) && this.data.orderType.fullDataset.length < 1)
    ) {
      this.data.orderType.fullDataset = this.api.loadGammaOrderTypeDataset();
    }

    // UPDATE ORDER TYPE DATASET
    this.data.orderType.dataset = Object.assign(
      Array<ICAutocompleteGenericModel>(),
      this.data.orderType.fullDataset,
    );

    // VALIDATE ORDER TYPE
    if (!this.control.isLoading && !this.control.isInitializing && this.icOrderType != null) {
      setTimeout(() => {
        this.icOrderType.forEach((c) => c.validateOrderType());
      }, 50);
    }
  }

  private initializeMarketTimes(): void {
    this.defaultsService.populateGoodTimeFieldSettings(this.data.goodFrom, this.data.goodUntil);
  }

  /**********************************************************************/
  /* SETTINGS METHODS */
  /**********************************************************************/

  /* SETTINGS - INITIALIZE */
  public onTabActivated(event: any): void {}

  public initialize(): void {
    let activeUser: ActiveUser = this.parentComponent.activeUser;
    this.activeSubComponent = this.parentComponent.activeSubCategory;
    if (activeUser.profileSettings) {
      if (activeUser.profileSettings.has(PROFILE_KEY)) {
        let savedData = activeUser.profileSettings.get(PROFILE_KEY);
        this.loadData(savedData);
      } else {
        this.control.isInitializing = false;
      }
    } else {
      this.control.isInitializing = false;
    }
  }

  /* SETTINGS - VALIDATE */
  public validate(): Promise<BfxUserSettingsValidityState> {
    return new Promise<BfxUserSettingsValidityState>((resolve, reject) => {
      let state = this.validateData();
      //logger.out.info(`${this.constructor.name}.validate() valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[state]}`);
      resolve(state);
    });
  }

  /* SETTINGS - SAVE */
  public save(request: CubiqTS.ProfileUpdateRequestImpl): void {
    this.saveData(request);
  }

  public postSave(request: CubiqTS.ProfileUpdateRequestImpl): void {
    let activeUser = this.parentComponent.activeUser;
    if (activeUser.profileSettings) {
      activeUser.profileSettings.set(PROFILE_KEY, this.entryDataToUpdate);
      activeUser.profileSettings.set(TAB_PROFILE_KEY, this.tabDataToUpdate);
    }
    this.dataChanged = false;
  }

  /* SETTINGS - RESET */
  public reset(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.resetData().then(() => {
        this.cd.detectChanges();
        resolve();
      });
    });
  }

  public postReset(): void {
    this.dataChanged = false;
    this.cd.detectChanges();
  }

  /* SETTINGS - CHANGE */
  private detectChanges(control: OENInputControl): boolean {
    let notLoading: boolean = !this.control.isLoading && !this.control.isInitializing;
    let state: BfxUserSettingsValidityState = this.validateData();
    return (
      notLoading &&
      (state == BfxUserSettingsValidityState.INVALID || this.detectModelChanges(control))
    );
  }

  private detectModelChanges(control: OENInputControl): boolean {
    if (control in OENInputControl) {
      switch (control) {
        case OENInputControl.CLIENT:
          if (
            this.data.client.value instanceof CubiqTS.ClientImpl &&
            this.loadedData.client.value instanceof CubiqTS.ClientImpl
          ) {
            return this.data.client.value.clientId !== this.loadedData.client.value.clientId;
          }
          return this.data.client.value !== this.loadedData.client.value;
        case OENInputControl.ACCOUNT:
          if (
            this.data.account.value instanceof CubiqTS.AccountImpl &&
            this.loadedData.account.value instanceof CubiqTS.AccountImpl
          ) {
            return this.data.account.value.id !== this.loadedData.account.value.id;
          }
          return this.data.account.value !== this.loadedData.account.value;
        case OENInputControl.CCY_PAIR:
          // Match either same string values or both null/undefined
          return this.data.currencyPair.product != this.loadedData.currencyPair.product;
        case OENInputControl.ORDER_TYPE:
          if (
            this.data.orderType.value instanceof ICAutocompleteGenericModel &&
            this.loadedData.orderType.value instanceof ICAutocompleteGenericModel
          ) {
            return this.data.orderType.value.value !== this.loadedData.orderType.value.value;
          }
          return this.data.orderType.value !== this.loadedData.orderType.value;
        case OENInputControl.CLIENT_INTENT:
          if (!this.control.showIntentAndAmount) {
            return false;
          } else if (this.data.clientIntent.value == null) {
            return true;
          } else if (
            this.data.clientIntent.value != null &&
            this.loadedData.clientIntent.value != null
          ) {
            return this.data.clientIntent.value !== this.loadedData.clientIntent.value;
          }
          return true;
        case OENInputControl.FIXED_CCY:
          if (
            !this.control.showIntentAndAmount ||
            typeof this.data.currencyPair.product !== "string"
          ) {
            return false;
          } else if (this.data.fixedCcy.value == null) {
            return true;
          } else if (
            this.data.fixedCcy.value instanceof ICFixedCcyModel &&
            this.loadedData.fixedCcy.value instanceof ICFixedCcyModel
          ) {
            return this.data.fixedCcy.value.ccy !== this.loadedData.fixedCcy.value.ccy;
          }
          return true;
        case OENInputControl.ORDER_AMOUNT:
          if (
            this.data.orderAmount.value instanceof CubiqTS.BigNumber &&
            this.loadedData.orderAmount.value instanceof CubiqTS.BigNumber
          ) {
            return !(this.data.orderAmount.value as CubiqTS.BigNumber).isEqualTo(
              this.loadedData.orderAmount.value,
            );
          }
          return this.data.orderAmount.value !== this.loadedData.orderAmount.value;
        case OENInputControl.VALUE_DATE:
          if (
            this.data.valueDate.value instanceof Object &&
            this.data.valueDate.value.hasOwnProperty("value") &&
            this.loadedData.valueDate.value instanceof Object &&
            this.loadedData.valueDate.value.hasOwnProperty("value")
          ) {
            return this.data.valueDate.value.value !== this.loadedData.valueDate.value.value;
          }
          return true;
        case OENInputControl.GOOD_FROM:
          if (
            this.data.goodFrom.value instanceof ICAutocompleteGenericModel &&
            this.loadedData.goodFrom.value instanceof ICAutocompleteGenericModel
          ) {
            return this.data.goodFrom.value.value !== this.loadedData.goodFrom.value.value;
          }
          return this.data.goodFrom.value !== this.loadedData.goodFrom.value;
        case OENInputControl.GOOD_UNTIL:
          if (
            this.data.goodUntil.value instanceof ICAutocompleteGenericModel &&
            this.loadedData.goodUntil.value instanceof ICAutocompleteGenericModel
          ) {
            return this.data.goodUntil.value.value !== this.loadedData.goodUntil.value.value;
          }
          return this.data.goodUntil.value !== this.loadedData.goodUntil.value;
        case OENInputControl.PRIVATE_ORDER:
          return this.data.privateOrder.value !== this.loadedData.privateOrder.value;
        case OENInputControl.DELTA:
          return this.data.delta.value !== this.loadedData.delta.value;
        case OENInputControl.RUNGS_ABOVE:
          return this.data.rungsAbove.value !== this.loadedData.rungsAbove.value;
        case OENInputControl.RUNGS_BELOW:
          return this.data.rungsBelow.value !== this.loadedData.rungsBelow.value;
        default:
          logger.out.warn(`${this.constructor.name}.detectChanges() unknown control ignored`);
          return true;
      }
    }
    return false;
  }

  private updateLoadedData(): void {
    this.loadedData = new OEDefaultsModel();
    // client
    this.loadedData.client.value =
      this.data.client.value instanceof CubiqTS.ClientImpl
        ? Object.assign(new CubiqTS.ClientImpl(), this.data.client.value)
        : null;
    // account
    this.loadedData.account.value =
      this.data.account.value instanceof CubiqTS.AccountImpl
        ? Object.assign(new CubiqTS.AccountImpl(), this.data.account.value)
        : null;
    // ccyPair
    this.loadedData.currencyPair.product = this.data.currencyPair.product;
    // orderType
    this.loadedData.orderType.value =
      this.data.orderType.value instanceof ICAutocompleteGenericModel
        ? Object.assign(new ICAutocompleteGenericModel(), this.data.orderType.value)
        : null;
    // clientIntent
    this.loadedData.clientIntent.value =
      this.control.showIntentAndAmount && this.data.clientIntent.value != null
        ? this.data.clientIntent.value
        : null;
    // ccy
    this.loadedData.fixedCcy.value =
      this.control.showIntentAndAmount && this.data.fixedCcy.value instanceof ICFixedCcyModel
        ? Object.assign(new ICFixedCcyModel(), this.data.fixedCcy.value)
        : null;
    // orderAmount
    this.loadedData.orderAmount.value =
      this.data.orderAmount.value instanceof CubiqTS.BigNumber
        ? new CubiqTS.BigNumber(this.data.orderAmount.value)
        : null;
    // valueDate
    this.loadedData.valueDate.value =
      this.data.valueDate.value instanceof ICAutocompleteGenericModel
        ? Object.assign(new ICAutocompleteGenericModel(), this.data.valueDate.value)
        : null;
    // goodFrom
    this.loadedData.goodFrom.value =
      this.data.goodFrom.value instanceof ICAutocompleteGenericModel
        ? Object.assign(new ICAutocompleteGenericModel(), this.data.goodFrom.value)
        : null;
    // goodUntil
    this.loadedData.goodUntil.value =
      this.data.goodUntil.value instanceof ICAutocompleteGenericModel
        ? Object.assign(new ICAutocompleteGenericModel(), this.data.goodUntil.value)
        : null;
    //private order
    this.loadedData.privateOrder.value = this.data.privateOrder.value;
    // delta
    this.loadedData.delta.value = this.data.delta.value;
    // rungs above
    this.loadedData.rungsAbove.value = this.data.rungsAbove.value;
    // rungs below
    this.loadedData.rungsBelow.value = this.data.rungsBelow.value;
  }

  public pageDataChanged() {
    // logger.out.warn(`ENTRY-DEFAULTS -> Loading: ${this.control.isLoading ? 'YES' : 'NO'} ` +
    //     `| Initializing: ${this.control.isInitializing ? 'YES' : 'NO'} ` +
    //     `| Data changed: ${this.dataChanged ? 'YES' : 'NO'} ` +
    //     `| OUTCOME: ${!this.control.isLoading && !this.control.isInitializing ? 'Continue' : 'Skip'}`);
    if (!this.control.isLoading && !this.control.isInitializing) {
      // register changes after loading is finished
      this.dataChanged = true;
      this.parentComponent.validate(this.categoryComponent);
    }
  }

  public hasDataChanged(): boolean {
    return this.dataChanged;
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {}

  public onDataChanged(event: any, items: Array<any>): void {}

  /**********************************************************************/
  /* SETTINGS METHODS - HELPERS */

  private getProfileSettingFromKey(
    profileSettingKey: string,
    request: CubiqTS.ProfileUpdateRequestImpl,
  ): CubiqTS.ProfileSettingImpl {
    let profileSettings: Array<any> = request.settings;
    if (!profileSettings) return;
    let savedOrderEntryPreferences: CubiqTS.ProfileSettingImpl = profileSettings.find((setting) => {
      return setting.profileSettingKey == profileSettingKey;
    });
    return savedOrderEntryPreferences;
  }

  // Handles Entry Data; Tabs are assumed valid
  private validateData(): BfxUserSettingsValidityState {
    let result: BfxUserSettingsValidityState;
    let checkStateFn = (
      state: ICNInputControlState | BfxInputControlStateEnum | BfxAutoCompleteComponentState,
    ) => {
      if (
        result === BfxUserSettingsValidityState.INVALID ||
        state === BfxInputControlStateEnum.INVALID
      ) {
        result = BfxUserSettingsValidityState.INVALID;
      } else if (
        result === BfxUserSettingsValidityState.VALID ||
        state === BfxInputControlStateEnum.VALID
      ) {
        result = BfxUserSettingsValidityState.VALID;
      } else if (state === BfxInputControlStateEnum.ACTIVE) {
        result = BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE;
      }
    };

    this.icClient.forEach((c) => checkStateFn(c.data.state));
    this.icAccount.forEach((c) => checkStateFn(c.data.state));
    this.icCcyPair.forEach((c) => checkStateFn(c.data.state));
    this.icOrderType.forEach((c) => checkStateFn(c.data.state));
    if (this.control.showIntentAndAmount) {
      this.icClientIntent.forEach((c) => checkStateFn(c.data.state));
      this.icOrderAmount.forEach((c) => checkStateFn(c.state));
      this.icFixedCcy.forEach((c) => checkStateFn(BfxInputControlStateEnum.VALID));
    }
    this.icValueDate.forEach((c) => checkStateFn(c.state));
    this.icGoodFrom.forEach((c) => checkStateFn(c.state));
    this.icGoodUntil.forEach((c) => checkStateFn(c.state));

    if (!this.validateDelta(this.data.delta.value)) {
      result = BfxUserSettingsValidityState.INVALID;
    }

    if (!this.validateRungsAbove(this.data.rungsAbove.value)) {
      result = BfxUserSettingsValidityState.INVALID;
    }

    if (!this.validateRungsBelow(this.data.rungsBelow.value)) {
      result = BfxUserSettingsValidityState.INVALID;
    }

    // icPrivateOrder can not be invalid
    // this.icPrivateOrder.forEach(c => result = result && c.data.state !== BfxInputControlStateEnum.INVALID);

    return result === BfxUserSettingsValidityState.INVALID
      ? result
      : this.dataChanged
        ? BfxUserSettingsValidityState.VALID
        : result;
  }

  // Handles both Entry Data and Tabs
  private saveData(request: CubiqTS.ProfileUpdateRequestImpl): void {
    // Entry Data
    let savedOrderEntryData: CubiqTS.ProfileSettingImpl = this.getProfileSettingFromKey(
      PROFILE_KEY,
      request,
    );
    if (savedOrderEntryData == null) {
      // save as new
      let entrySettingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
      entrySettingObject.profileSettingKey = PROFILE_KEY;
      entrySettingObject.profileSettingValue = this.generateEntryData();
      request.settings.push(entrySettingObject);
    } else {
      // save as update
      savedOrderEntryData.profileSettingValue = this.generateEntryData();
    }

    this.updateLoadedData();
  }
  public resetDataTest(): void {
    this.resetData();
  }

  // Handles both Entry Data and Tabs
  private resetData(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.control.isInitializing = true;
      // DATA
      // reset controls
      this.icClient.forEach((c) => c.reset());
      this.icAccount.forEach((c) => c.reset());
      this.icCcyPair.forEach((c) => c.resetInput());
      this.icOrderType.forEach((c) => c.reset());
      this.icClientIntent.forEach((c) => c.resetInputControl());
      this.icFixedCcy.forEach((c) => c.reset());
      this.icOrderAmount.forEach((c) => c.resetInputControl());
      this.icGoodFrom.forEach((c) => c.reset());
      this.icGoodUntil.forEach((c) => c.reset());
      this.icValueDate.forEach((c) => c.reset());

      this.data.valueDate.value = null;
      this.data.goodFrom.value = null;
      this.data.goodUntil.value = null;
      this.icPrivateOrder.forEach((c) => c.reset());
      this.data.client.accountConstraint = null;

      this.data.delta.minValue = 0;
      this.data.delta.precision = 1;
      this.data.delta.value = this.data.delta.oldValue = null;
      this.data.rungsAbove.value = this.data.rungsAbove.oldValue = null;
      this.data.rungsBelow.value = this.data.rungsBelow.oldValue = null;

      if (
        this.rungsAboveControl != null &&
        this.rungsAboveControl.first != null &&
        this.rungsAboveControl.first.writeValue != null
      ) {
        this.rungsAboveControl.first.writeValue(this.data.rungsAbove.value);
      }

      if (
        this.rungsBelowControl != null &&
        this.rungsBelowControl.first != null &&
        this.rungsBelowControl.first.writeValue != null
      ) {
        this.rungsBelowControl.first.writeValue(this.data.rungsBelow.value);
      }

      // re - initialize
      let activeUser: ActiveUser = this.parentComponent.activeUser;
      if (activeUser.profileSettings && activeUser.profileSettings.has(PROFILE_KEY)) {
        let savedData = activeUser.profileSettings.get(PROFILE_KEY);
        this.loadData(savedData);
      } else {
        this.control.isInitializing = false;
      }
      resolve();
    });
  }

  public generateEntryData(): string {
    let entryData = {
      client:
        this.data.client.value instanceof CubiqTS.ClientImpl
          ? this.data.client.value.clientId
          : null,
      account:
        this.data.account.value instanceof CubiqTS.AccountImpl ? this.data.account.value.id : null,
      ccyPair:
        typeof this.data.currencyPair.product === "string" &&
        this.data.currencyPair.product.length === 6
          ? this.data.currencyPair.product
          : null,
      orderType:
        this.data.orderType.value instanceof ICAutocompleteGenericModel
          ? this.data.orderType.value.value
          : null,
      clientIntent:
        this.control.showIntentAndAmount && this.data.clientIntent.value != null
          ? this.data.clientIntent.value
          : null,
      ccy:
        this.control.showIntentAndAmount &&
        typeof this.data.currencyPair.product === "string" &&
        this.data.fixedCcy.value instanceof ICFixedCcyModel
          ? this.data.fixedCcy.value.ccy
          : null,
      orderAmount:
        this.data.orderAmount.value instanceof CubiqTS.BigNumber
          ? this.data.orderAmount.value.toFixed(2)
          : null,
      valueDate:
        this.data.valueDate.value instanceof ICAutocompleteGenericModel
          ? this.data.valueDate.value.value
          : null,
      goodFrom:
        this.data.goodFrom.value instanceof ICAutocompleteGenericModel
          ? this.data.goodFrom.value.value
          : null,
      goodUntil:
        this.data.goodUntil.value instanceof ICAutocompleteGenericModel
          ? this.data.goodUntil.value.value
          : null,
      privateOrder: this.data.privateOrder.value,
      delta: this.data.delta.value,
      rungsAbove: this.data.rungsAbove.value,
      rungsBelow: this.data.rungsBelow.value,
    };
    this.entryDataToUpdate = JSON.stringify(entryData);
    return this.entryDataToUpdate;
  }

  private loadData(savedData: any): void {
    try {
      this.cd.detach();
      let parsedData = JSON.parse(savedData);
      this.control.clientAccountConstraintAutoSelect = false;
      if (parsedData != null) {
        this.control.isLoading = true;
        this.control.isInitializing = false;

        let clientIsSet: boolean = false;
        let ccyPairIsSet: boolean = false;
        let orderTypeIsSet: boolean = false;

        let promiseArray = new Array<Promise<any>>();
        // client (promise [0])
        if (parsedData.hasOwnProperty("client") && typeof parsedData.client === "string") {
          promiseArray.push(
            this.clientLookup
              .getActiveClient(parsedData.client, ClientAccountVisibilityType.Full)
              .catch((error) => {
                logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                return null;
              }),
          );
        } else {
          // dummy promise to keep up appearances
          promiseArray.push(
            new Promise<null>((resolve) => {
              resolve(null);
            }),
          );
        }
        // account (promise [1])
        if (parsedData.hasOwnProperty("account") && typeof parsedData.account === "string") {
          promiseArray.push(
            this.accountLookup
              .getActiveAccount(parsedData.account, ClientAccountVisibilityType.Full)
              .catch((error) => {
                logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                return null;
              }),
          );
        } else {
          promiseArray.push(
            new Promise<null>((resolve) => {
              resolve(null);
            }),
          );
        }
        // ccyPair
        if (
          parsedData.hasOwnProperty("ccyPair") &&
          typeof parsedData.ccyPair === "string" &&
          parsedData.ccyPair.length === 6
        ) {
          this.data.currencyPair.product = parsedData.ccyPair;
          this.icCcyPair.forEach((c) => c.setCcyPairExternally(parsedData.ccyPair));
          this.onCurrencyPairChange(null, true);
          ccyPairIsSet = true;

          // Pair no longer available in gamma pair subset
          if (this.data.currencyPair.product == null && parsedData.ccyPair != null) {
            this.icCcyPair.forEach((c) => (c.data.state = ICNInputControlState.INVALID));
          }
        } else {
          this.data.valueDate.disabled = true;
        }
        // orderType
        if (
          parsedData.hasOwnProperty("orderType") &&
          typeof parsedData.orderType === "number" &&
          this.data.orderType.dataset instanceof Array
        ) {
          let orderTypeModel = this.data.orderType.fullDataset.find(
            (item) => item.value === parsedData.orderType,
          );
          if (orderTypeModel instanceof ICAutocompleteGenericModel) {
            this.icOrderType.forEach((c) => c.setDataValue(orderTypeModel, true, true, true));
            orderTypeIsSet = true;
          } else {
            logger.out.warn(
              `WARNING: spotDefaults.loadData(): Order Type is present (${parsedData.orderType}) but the ICAutocompleteGenericModel model is not found!`,
            );
          }
        }
        // clientIntent
        if (
          parsedData.hasOwnProperty("clientIntent") &&
          typeof parsedData.clientIntent === "number" &&
          this.icClientIntent != null
        ) {
          this.icClientIntent.forEach((c) => c.writeValue(parsedData.clientIntent));
        }
        // ccy
        if (
          ccyPairIsSet &&
          parsedData.hasOwnProperty("ccy") &&
          typeof parsedData.ccy === "string" &&
          parsedData.ccy.length === 3
        ) {
          let componentModel = new ICFixedCcyModel(parsedData.ccy, parsedData.ccyPair);
          this.icFixedCcy.forEach((c) => c.load(componentModel, false));
          this.data.fixedCcy.ccyPair = parsedData.ccyPair;
          this.data.fixedCcy.fixedCcy = parsedData.ccy;
          if (this.data.fixedCcy.value == null) {
            this.data.fixedCcy.value = componentModel;
          }
        } else if (
          ccyPairIsSet &&
          parsedData &&
          parsedData.ccy == null &&
          this.data.currencyPair != null &&
          this.data.currencyPair.base != null
        ) {
          let ccy = this.data.currencyPair.base;
          this.icFixedCcy.forEach((c) =>
            c.load(new ICFixedCcyModel(ccy, parsedData.ccyPair), false),
          );
          this.data.fixedCcy.ccyPair = parsedData.ccyPair;
          this.data.fixedCcy.fixedCcy = ccy;
        }
        // orderAmount
        if (
          parsedData.hasOwnProperty("orderAmount") &&
          typeof parsedData.orderAmount === "string"
        ) {
          this.data.orderAmount.value = new CubiqTS.BigNumber(parsedData.orderAmount);
        }
        // value date
        if (parsedData.hasOwnProperty("valueDate") && typeof parsedData.valueDate === "number") {
          if (ccyPairIsSet) {
            let valueDateModel = this.data.valueDate.dataset.find(
              (item) => item.value === parsedData.valueDate,
            );
            if (valueDateModel instanceof ICAutocompleteGenericModel) {
              this.data.valueDate.value = valueDateModel;
            } else {
              logger.out.warn(
                `WARNING: spotDefaults.loadData(): Value Date is present (${parsedData.valueDate}) but the ICAutocompleteGenericModel model is not found in dataset!`,
              );
            }
          } else {
            this.data.valueDate.value = null;
          }
        }
        // goodFrom
        if (parsedData.hasOwnProperty("goodFrom") && typeof parsedData.goodFrom === "string") {
          let goodFromModel = this.data.goodFrom.allDataset.find(
            (item) => item.value === parsedData.goodFrom,
          );
          if (goodFromModel instanceof ICAutocompleteGenericModel) {
            this.data.goodFrom.value = goodFromModel;
          } else {
            logger.out.warn(
              `WARNING: spotDefaults.loadData(): Good From is present (${parsedData.goodFrom}) but the ICAutocompleteGenericModel model is not found in dataset!`,
            );
          }
        }
        // goodUntil
        if (parsedData.hasOwnProperty("goodUntil") && typeof parsedData.goodUntil === "string") {
          let goodUntilModel = this.data.goodUntil.allDataset.find(
            (item) => item.value === parsedData.goodUntil,
          );
          if (goodUntilModel instanceof ICAutocompleteGenericModel) {
            this.data.goodUntil.value = goodUntilModel;
          } else {
            logger.out.warn(
              `WARNING: spotDefaults.loadData(): Good Until is present (${parsedData.goodUntil}) but the ICAutocompleteGenericModel model is not found in dataset!`,
            );
          }
        }
        // privateOrder
        if (
          parsedData.hasOwnProperty("privateOrder") &&
          typeof parsedData.privateOrder === "boolean"
        ) {
          this.data.privateOrder.value = parsedData.privateOrder;
          this.icPrivateOrder.forEach((c) => c.load(parsedData.privateOrder));
        }

        // Delta
        if (parsedData.hasOwnProperty("delta") && typeof parsedData.delta === "number") {
          this.data.delta.value = parsedData.delta;
          this.data.delta.oldValue = parsedData.delta;
        }

        // Rungs above
        if (parsedData.hasOwnProperty("rungsAbove") && typeof parsedData.rungsAbove === "number") {
          this.data.rungsAbove.value = parsedData.rungsAbove;
          this.data.rungsAbove.oldValue = parsedData.rungsAbove;
          this.rungsAboveControl.first.writeValue(this.data.rungsAbove.value);
        }

        // Rungs below
        if (parsedData.hasOwnProperty("rungsBelow") && typeof parsedData.rungsBelow === "number") {
          this.data.rungsBelow.value = parsedData.rungsBelow;
          this.data.rungsBelow.oldValue = parsedData.rungsBelow;
          this.rungsBelowControl.first.writeValue(this.data.rungsBelow.value);
        }

        Promise.all(promiseArray).then((values: Array<any>) => {
          // assign values
          let client = values[0];
          let account = values[1];

          // client
          if (client != null && client instanceof CubiqTS.ClientImpl) {
            this.data.client.value = client;
            this.icClient.forEach((c) => c.setDataValue(client, true, false, true, false));
            clientIsSet = true;
          } else if (parsedData.hasOwnProperty("client") && typeof parsedData.client === "string") {
            logger.out.warn(
              `WARNING: spotDefaults.loadData(): Invalid client for '${parsedData.client}'`,
            );
          }

          setTimeout(
            () => {
              // load account after input_constrain & dataset is resolved
              if (account != null && account instanceof CubiqTS.AccountImpl) {
                this.data.account.value = account;
                this.icAccount.forEach((c) => c.setDataValue(account, true, false, true, false));
              } else if (
                parsedData.hasOwnProperty("account") &&
                typeof parsedData.account === "string"
              ) {
                logger.out.warn(
                  `WARNING: spotDefaults.loadData(): Invalid client for '${parsedData.client}'`,
                );
              }

              // Dealing with MKT order type
              if (clientIsSet && ccyPairIsSet) {
                this.initializeOrderTypeDataset();
              } else if (orderTypeIsSet) {
                this.icOrderType.forEach((c) => c.validateOrderType());
              }

              this.updateLoadedData();
              this.initializeMarketTimes();

              // end loading
              setTimeout(() => {
                this.control.isLoading = false;
                this.control.clientAccountConstraintAutoSelect = true;
                this.parentComponent.validate(this.categoryComponent, true, true).then(() => {
                  this.cd.detectChanges();
                });
              }, 100);
            },
            account == null ? 4 : 100,
          );
        });
      } else {
        this.control.isLoading = false;
        this.control.isInitializing = false;
        this.control.clientAccountConstraintAutoSelect = true;
      }
    } catch (ex) {
      // fallback
      this.control.isLoading = false;
      this.control.isInitializing = false;
      this.control.clientAccountConstraintAutoSelect = true;
    } finally {
      this.cd.detectChanges();
      this.cd.reattach();
    }
  }

  /**********************************************************************/
  /* VALUE CHANGE HANDLERS */

  // Client
  public onClientChange(dataFromInput: any): void {
    this.initializeOrderTypeDataset();
    if (this.detectChanges(OENInputControl.CLIENT)) {
      this.pageDataChanged();
    }
  }

  public onClientSelect($event: any): void {
    if (this.detectChanges(OENInputControl.CLIENT)) {
      this.pageDataChanged();
    }
  }

  public onClientTabChange($event): void {
    if (this.detectChanges(OENInputControl.CLIENT_ACTIVE_TAB)) {
      this.pageDataChanged();
    }
  }
  // Account
  public onAccountChange(dataFromInput: any) {
    this.cd.detach();
    this.data.account.value = dataFromInput;
    setTimeout(() => {
      this.data.client.accountConstraint = this.data.account.value;
    }, 0);
    if (this.detectChanges(OENInputControl.ACCOUNT)) {
      this.pageDataChanged();
    }
    this.cd.detectChanges();
    this.cd.reattach();
  }

  public onAccountTabChange($event): void {
    if (this.detectChanges(OENInputControl.ACCOUNT_ACTIVE_TAB)) {
      this.pageDataChanged();
    }
  }

  // Ccy pair
  public onCurrencyPairChange($event: any, fromLoad: boolean = false): void {
    let productCode: string = this.data.currencyPair.product;
    let productCodeValid: boolean = false;

    if (typeof productCode === "string" && productCode.length === 6) {
      // validate
      productCodeValid =
        this.data.currencyPair.dataset.find(
          (item: string) => item.toLowerCase() === productCode.toLowerCase(),
        ) != null;
      if (productCodeValid) {
        // LOAD CCY MODEL
        let currencyPairLookup: CcyPairLookupService = this.currencyPairService.getBase();
        this.data.currencyPair.product = productCode;
        this.data.currencyPair.model = currencyPairLookup.getItem(productCode);
        this.data.currencyPair.inverted = false;
        // check for invert model
        if (this.data.currencyPair.model == null) {
          this.data.currencyPair.model = currencyPairLookup.getCcyPair(
            productCode.substr(3, 3),
            productCode.substr(0, 3),
          );
          this.data.currencyPair.inverted = this.data.currencyPair.model != null;
        }

        // ALLOCATE BASE / NON-BASE
        if (this.data.currencyPair.inverted) {
          this.data.currencyPair.base = productCode.substr(3, 3);
          this.data.currencyPair.nonBase = productCode.substr(0, 3);
        } else {
          this.data.currencyPair.base = productCode.substr(0, 3);
          this.data.currencyPair.nonBase = productCode.substr(3, 3);
        }

        // PROPAGATE FIXED CCY
        this.data.fixedCcy.ccyPair = productCode;
      } else {
        this.data.currencyPair.model = null;
      }
    } else {
      this.data.currencyPair.model = null;
    }

    this.initializeOrderTypeDataset();
    // ccyPair invalid or null
    if (!productCodeValid) {
      // RESET FIXED CCY
      this.icFixedCcy.forEach((c) => c.reset());
      this.data.fixedCcy.ccyPair = null;
      this.data.fixedCcy.fixedCcy = null;
      // DISABLE VALUE DATE
      //this.data.valueDate.value = this.defaultValueDate;
      this.data.valueDate.disabled = true;
    }

    if (this.data.currencyPair.model != null) {
      let minDelta: number = this.data.currencyPair.model.minimumGammaDeltaPips.toNumber();
      this.data.delta.minValue = minDelta;
      if (this.validateDelta(this.data.delta.value)) {
        this.data.delta.externalState = ICNInputControlState.VALID;
      } else {
        this.data.delta.externalState = ICNInputControlState.INVALID;
      }
    } else {
      this.data.delta.minValue = 0;
      this.data.delta.externalState = ICNInputControlState.VALID;
    }

    if (this.detectChanges(OENInputControl.CCY_PAIR)) {
      this.pageDataChanged();
    }
  }

  // Order Type
  public onOrderTypeChange($event: any) {
    if (
      this.data.orderType.value instanceof ICAutocompleteGenericModel &&
      this.data.orderType.value.value === CubiqTS.RequestOrderType.CALL
    ) {
      this.control.showIntentAndAmount = false;
    } else {
      this.control.showIntentAndAmount = true;
    }

    if (this.detectChanges(OENInputControl.ORDER_TYPE)) {
      this.pageDataChanged();
    }
  }

  // Client Intent
  public onClientIntentToggleChange(dataFromInput: any) {
    if (this.detectChanges(OENInputControl.CLIENT_INTENT)) {
      this.pageDataChanged();
    }
  }

  // Fixed Ccy
  public onFixedCcyToggleChange(dataFromInput: any) {
    if (
      this.data.fixedCcy.value instanceof ICFixedCcyModel &&
      typeof this.data.fixedCcy.value.ccy === "string"
    ) {
      this.data.fixedCcy.fixedCcy = this.data.fixedCcy.value.ccy;
    } else {
      this.data.fixedCcy.fixedCcy = null;
    }

    if (this.detectChanges(OENInputControl.FIXED_CCY)) {
      this.pageDataChanged();
    }
  }

  // Order Amount
  public onOrderAmountChange(dataFromInput: any) {
    if (this.detectChanges(OENInputControl.ORDER_AMOUNT)) {
      this.pageDataChanged();
    }
  }

  public onOrderAmountBlur(dataFromInput: any) {
    if (this.detectChanges(OENInputControl.ORDER_AMOUNT)) {
      this.pageDataChanged();
    }
  }
  // Value date
  public valueDateOnBlur($event) {
    if (this.detectChanges(OENInputControl.VALUE_DATE)) {
      this.pageDataChanged();
    } else {
      this.parentComponent.validate(this.categoryComponent);
    }
  }
  // Good From
  public onGoodFromChange($event) {
    if (this.detectChanges(OENInputControl.GOOD_FROM)) {
      this.pageDataChanged();
    } else {
      this.parentComponent.validate(this.categoryComponent);
    }
  }
  // Good Until
  public onGoodUntilChange($event) {
    if (this.detectChanges(OENInputControl.GOOD_UNTIL)) {
      this.pageDataChanged();
    } else {
      this.parentComponent.validate(this.categoryComponent);
    }
  }
  // Private Order
  public onPrivateOrderChange() {
    if (this.detectChanges(OENInputControl.PRIVATE_ORDER)) {
      this.pageDataChanged();
    }
  }

  public onUntilConstraintValidityChange(dataFromInput: any) {}

  /**********************************************************************/
  /* VALUE DATE - METHODS */

  public valueDateSuggestion(event: any) {
    if (event.query != null && typeof event.query === "string" && event.query.length > 0) {
      this.data.valueDate.suggestions = Object.assign(
        new Array<ICAutocompleteGenericModel>(),
        this.data.valueDate.dataset.filter((item) =>
          item.label.toLowerCase().includes(event.query.toLowerCase()),
        ),
      );
    } else {
      this.data.valueDate.suggestions = Object.assign([], this.data.valueDate.dataset);
    }
  }

  /**********************************************************************/
  /* GOOD FROM - METHODS */

  public goodFromSuggestion(event: any): void {
    this.data.goodFrom.suggestions = [];
    for (let marketTime of this.data.goodFrom.validDataset) {
      if (marketTime.label.toLowerCase().indexOf(event.query.toLowerCase()) >= 0) {
        this.data.goodFrom.suggestions.push(marketTime);
      }
    }
  }

  /**********************************************************************/
  /* GOOD FROM - METHODS */

  public goodUntilSuggestion(event: any): void {
    this.data.goodUntil.suggestions = [];
    for (let marketTime of this.data.goodUntil.validDataset) {
      if (marketTime.label.toLowerCase().indexOf(event.query.toLowerCase()) >= 0) {
        this.data.goodUntil.suggestions.push(marketTime);
      }
    }
  }

  public onDeltaChanged(value: any): void {
    if (this.validateDelta(value)) {
      this.data.delta.oldValue = this.data.delta.value;
      this.data.delta.externalState = ICNInputControlState.VALID;
    } else if (this.validateDelta(this.data.delta.oldValue)) {
      this.data.delta.value = this.data.delta.oldValue;
      this.deltaControl.first.writeValue(this.data.delta.value);
      this.data.delta.externalState = ICNInputControlState.VALID;
    } else {
      this.data.delta.oldValue = value;
      this.data.delta.value = value;
      this.data.delta.externalState = ICNInputControlState.INVALID;
    }

    if (this.detectChanges(OENInputControl.DELTA)) {
      this.pageDataChanged();
    }
  }

  public onRungsAboveChanged(value: any): void {
    if (this.validateRungsAbove(value)) {
      this.data.rungsAbove.oldValue = this.data.rungsAbove.value;
      this.rungsAboveControl.first.errorMsg = null;
      this.rungsBelowControl.first.errorMsg = null;
    } else if (this.validateRungsAbove(this.data.rungsAbove.oldValue)) {
      this.data.rungsAbove.value = this.data.rungsAbove.oldValue;
      this.rungsAboveControl.first.writeValue(this.data.rungsAbove.value);
      this.data.rungsAbove.externalState = ICNInputControlState.VALID;
    } else {
      this.data.rungsAbove.oldValue = value;
      this.data.rungsAbove.value = value;
      this.data.rungsAbove.externalState = ICNInputControlState.INVALID;
    }

    if (!Number.isNaN(this.data.rungsAbove.value)) {
      this.data.rungsBelow.max = this.data.rungsAbove.maxRungs - Number(this.data.rungsAbove.value);
    }

    if (this.detectChanges(OENInputControl.RUNGS_ABOVE)) {
      this.pageDataChanged();
    }
  }

  public onRungsBelowChanged(value: any): void {
    if (this.validateRungsBelow(value)) {
      this.data.rungsBelow.oldValue = this.data.rungsBelow.value;
      this.rungsAboveControl.first.errorMsg = null;
      this.rungsBelowControl.first.errorMsg = null;
    } else if (this.validateRungsBelow(this.data.rungsBelow.oldValue)) {
      this.data.rungsBelow.value = this.data.rungsBelow.oldValue;
      this.rungsBelowControl.first.writeValue(this.data.rungsBelow.value);
      this.data.rungsBelow.externalState = ICNInputControlState.VALID;
    } else {
      this.data.rungsBelow.oldValue = value;
      this.data.rungsBelow.value = value;
      this.data.rungsBelow.externalState = ICNInputControlState.INVALID;
    }

    if (!Number.isNaN(this.data.rungsBelow.value)) {
      this.data.rungsAbove.max = this.data.rungsAbove.maxRungs - Number(this.data.rungsBelow.value);
    }

    if (this.detectChanges(OENInputControl.RUNGS_BELOW)) {
      this.pageDataChanged();
    }
  }

  protected validateDelta(value: number): boolean {
    let valid: boolean = true;

    if (value != null && Number.isNaN(value)) {
      valid = false;
    } else if (value != null && this.data.currencyPair.model instanceof CubiqTS.CurrencyPairImpl) {
      let currencyPair: CubiqTS.CurrencyPairImpl = this.data.currencyPair.model;

      if (
        currencyPair.minimumGammaDeltaPips &&
        currencyPair.minimumGammaDeltaPips.toNumber &&
        value < currencyPair.minimumGammaDeltaPips.toNumber()
      ) {
        valid = false;
      }
    }

    return valid;
  }

  protected validateRungsAbove(value: number): boolean {
    let valid: boolean = true;

    if (value != null && Number.isNaN(value)) {
      valid = false;
    } else if (value != null) {
      let rungsBelow: number = 0;
      let rungsAbove: number = Number(value);

      if (this.data.rungsBelow.value != null && !Number.isNaN(this.data.rungsBelow.value)) {
        rungsBelow = Number(this.data.rungsBelow.value);
      }

      let totalRungs: number = rungsAbove + rungsBelow;
      valid = totalRungs > 0 && totalRungs <= this.data.rungsAbove.maxRungs;
    }

    return valid;
  }

  protected validateRungsBelow(value: number): boolean {
    let valid: boolean = true;

    if (value != null && Number.isNaN(value)) {
      valid = false;
    } else if (value != null) {
      let rungsBelow: number = Number(value);
      let rungsAbove: number = 0;

      if (this.data.rungsAbove.value != null && !Number.isNaN(this.data.rungsAbove.value)) {
        rungsAbove = Number(this.data.rungsAbove.value);
      }

      let totalRungs: number = rungsAbove + rungsBelow;
      valid = totalRungs > 0 && totalRungs <= this.data.rungsAbove.maxRungs;
    }

    return valid;
  }

  protected getAdditionalPrecision(): number {
    let currencyPair: CubiqTS.CurrencyPairImpl = this.data.currencyPair.model;
    return this.data.currencyPair.inverted
      ? currencyPair.limitEntrySpotInvertDecimals - currencyPair.spotInvertDecimalsQty
      : currencyPair.limitEntrySpotDecimals - currencyPair.pipsQty;
  }
}
